"use client";

import { useUser } from "@clerk/nextjs";
import { useQueryState } from "nuqs";
import Link from "next/link";
import { RulerSquareIcon } from "@radix-ui/react-icons";
import {
  ArrowTopRightOnSquareIcon,
  CameraIcon,
  DocumentIcon,
  HomeIcon,
  InformationCircleIcon,
  MapPinIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline";
import { Fragment, type PropsWithChildren } from "react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CardEmpty } from "@/app/_components/card-empty";
import { AdSenseResponsive } from "@/app/_components/adsense-responsive";
import { ViabilityList } from "@/app/_components/viability-calculator/viability-list/viability-list";
import { Registry } from "./tabs/registry";
import { Diligence } from "./tabs/diligence";
import { Notice } from "./tabs/notice";

import { ImageWithFallback } from "./image";
import { ImageGallery } from "./image";
import { FavoriteButton } from "./favorite";
import { NotFound } from "./not-found";
import { CreateRegistry } from "./create-registry";
import { CreateNotice } from "./create-notice";
import { AuctionDetails } from "./auction-details";
import { InfoDialog } from "./info-dialog";

import { upperFirst } from "@/helper/string";

import {
  formatPropertyDescriptionJsonLd,
  formatPropertyRegistrationUrl,
  formatPropertyTitle,
  formatPropertyNoticeUrl,
  generateKeywords,
} from "@/helper/property";
import { getUserPlanByMetadata } from "@/helper/user/client";
import { cn } from "@/lib/utils";

import {
  bankCriteria,
  banks,
  modalityReverseCriteria,
  TABS_ESTATE,
} from "@/constants";
import { propertyTabSearchParam } from "@/filters";

import { formatBRL } from "@/helper/currency";

import telegramGroup from "@/static/telegram-group.json";

import type { BankCriteria, House, Modality, TabsEstate } from "@/types";

interface EstateTitleProps {
  children?: React.ReactNode;
  endAdornment?: React.ReactNode;
  title: string;
}

export const EstateTitle = ({
  children,
  endAdornment,
  title,
}: EstateTitleProps) => (
  <div className="flex flex-col gap-7">
    <div className="flex flex-col gap-2">
      <div className="flex gap-2 items-center">
        <p className="text-xl font-semibold">{title}</p>
        {endAdornment}
      </div>
      <hr />
    </div>
    {children}
  </div>
);

const RegisterTooltip = () => (
  <Tooltip>
    <TooltipTrigger asChild>
      <InformationCircleIcon className="size-4" aria-hidden="true" />
    </TooltipTrigger>
    <TooltipContent>
      <p>Informação não informada, verifique no edital do leilão.</p>
    </TooltipContent>
  </Tooltip>
);

interface OpenLinkOutsideProps {
  href: string;
  display?: "inline-flex" | "block";
}

const OpenLinkOutside = ({
  children,
  href,
  display = "inline-flex",
}: PropsWithChildren<OpenLinkOutsideProps>) => (
  <span className={cn("items-center gap-1", display)}>
    {children}
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="p-2 min-h-4"
          aria-label="Você será redirecionado para outro site."
          onClick={() => window.open(href, "_blank")}
        >
          <ArrowTopRightOnSquareIcon className="h-3 w-3" aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Você será redirecionado para outro site.</TooltipContent>
    </Tooltip>
  </span>
);

const bankDescription = {
  [banks.caixa.value]:
    "Leilões de Imóveis da Caixa Econômica Federal - A Caixa oferece imóveis por meio de modalidades como Venda Direta e Leilão Extrajudicial. Esses imóveis passam por análises jurídicas rigorosas, assegurando segurança ao comprador. É possível financiar até 95% do valor do imóvel e utilizar o FGTS no pagamento (sempre leia o edital para confirmar se será possível usar ou não o FGTS/financimento), conforme as condições estabelecidas pelo banco. A Lei de Alienação Fiduciária respalda essas operações, proporcionando maior tranquilidade aos investidores.",
  [banks.santander.value]:
    "Leilões de Imóveis do Banco Santander - O Santander disponibiliza imóveis em leilões de Venda Direta e Leilão Extrajudicial, frequentemente com preços abaixo do valor de mercado. Os compradores podem contar com condições facilitadas de financiamento e a possibilidade de utilizar o FGTS (sempre leia o edital para confirmar se será possível usar ou não o FGTS/financimento), conforme as regras vigentes. Todos os imóveis são submetidos a análises jurídicas detalhadas, garantindo segurança nas transações.",
  [banks.bradesco.value]:
    "Leilões de Imóveis do Banco Bradesco - O Bradesco oferece imóveis em leilões de Venda Direta e Leilão Extrajudicial, com preços geralmente abaixo do valor de mercado. Os compradores podem contar com condições facilitadas de financiamento e a possibilidade de utilizar o FGTS (sempre leia o edital para confirmar se será possível usar ou não o FGTS/financimento), conforme as regras vigentes. Todos os imóveis são submetidos a análises jurídicas detalhadas, garantindo segurança nas transações.",
} satisfies Record<BankCriteria, string>;

interface DialogProps {
  data: House;
  showRegion?: boolean;
}

export const Property = ({ data, showRegion }: DialogProps) => {
  if (!data) return <NotFound />;

  const { user } = useUser();
  const { hasPaidPlan } = getUserPlanByMetadata(user?.publicMetadata);

  const {
    uf,
    endereco,
    bairro,
    cidade_descricao,
    preco_atual = 0,
    valor_de_avaliacao = 0,
    ...otherData
  } = data;

  const [activeTab, setActiveTab] = useQueryState(propertyTabSearchParam.aba, {
    history: "replace",
    defaultValue: TABS_ESTATE.imovel.value,
    parse: (value) => value as TabsEstate,
  });

  const zapUrl = `https://www.google.com/search?q=site:zapimoveis.com.br ${otherData.tipo} em ${uf}, ${bairro}, ${cidade_descricao}`;
  const vivarealUrl = `https://www.google.com/search?q=site:vivareal.com.br ${otherData.tipo} em ${uf}, ${bairro}, ${cidade_descricao}`;
  const imovelwebUrl = `https://www.google.com/search?q=site:imovelweb.com.br ${otherData.tipo} em ${uf}, ${bairro}, ${cidade_descricao}`;
  const mapUrl = `http://maps.google.com/?q=${endereco}, ${bairro} - ${cidade_descricao} - ${uf}`;
  const matriculaUrl = formatPropertyRegistrationUrl({ ...otherData, uf });
  const editalUrl = formatPropertyNoticeUrl({
    banco: otherData.banco,
    edital: otherData.edital,
  });
  const iptuUrl = `https://www.google.com/search?q=consultar+IPTU+${cidade_descricao}+${uf}`;

  const open = (value: TabsEstate) => {
    setActiveTab(value);
  };

  const close = () => {
    setActiveTab(null);
  };

  const scrollToTop = () => {
    window.scrollTo(0, 0);
  };

  const mainImage = otherData.imagens?.length > 0 ? otherData.imagens[0] : "";
  const title = formatPropertyTitle(
    {
      tipo: otherData.tipo,
      cidade_descricao,
      bairro,
      banco: otherData.banco,
    },
    {
      showBank: true,
      showAuction: true,
    }
  );
  const alt = formatPropertyDescriptionJsonLd({
    cidade_descricao,
    bairro,
    endereco,
    tipo: otherData.tipo,
    banco: otherData.banco,
    total_area: otherData.total_area,
    total_area_privativa: otherData.total_area_privativa,
    total_area_terreno: otherData.total_area_terreno,
    uf,
  });

  const hasGallery =
    otherData.banco !== banks.caixa.value && otherData.imagens?.length > 1;

  return (
    <main className="container p-0 mx-auto flex flex-col gap-8">
      <div className="scroll-mt-36">
        <a href={mapUrl} target="_blank" rel="nofollow">
          <h1 className="text-2xl font-semibold">
            {title}{" "}
            <OpenLinkOutside href={mapUrl} display="block">
              <span className="text-base font-normal text-muted-foreground hover:underline">
                {endereco}
              </span>
            </OpenLinkOutside>
          </h1>
        </a>
      </div>
      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          setActiveTab(value as TabsEstate);
        }}
      >
        <TabsList className="mb-8">
          <TabsTrigger value={TABS_ESTATE.imovel.value}>
            {TABS_ESTATE.imovel.label}
          </TabsTrigger>
          <TabsTrigger value={TABS_ESTATE.calculadora.value}>
            {TABS_ESTATE.calculadora.label}
          </TabsTrigger>
          <TabsTrigger value={TABS_ESTATE.matricula.value}>
            {TABS_ESTATE.matricula.label}
          </TabsTrigger>
          <TabsTrigger value={TABS_ESTATE.edital.value}>
            {TABS_ESTATE.edital.label}
          </TabsTrigger>
          <TabsTrigger value={TABS_ESTATE.diligencia.value}>
            {TABS_ESTATE.diligencia.label}
          </TabsTrigger>
        </TabsList>
        <TabsContent value={TABS_ESTATE.imovel.value}>
          <main className="w-full flex flex-col gap-8">
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
              {/* Left Column - Property Details */}
              <div className="lg:col-span-2 flex flex-col gap-10">
                <div className="overflow-hidden bg-white rounded-lg shadow-md relative">
                  <div
                    role="button"
                    aria-label="Abrir fotos"
                    className="flex h-[200px] sm:h-[400px] sm:gap-2"
                    onClick={() => open(TABS_ESTATE.fotos.value)}
                  >
                    <div
                      className={cn(
                        "w-full relative",
                        hasGallery ? "sm:w-9/12" : ""
                      )}
                    >
                      <ImageWithFallback
                        src={mainImage}
                        alt={alt}
                        className="object-cover w-full h-full"
                      />
                    </div>
                    {hasGallery ? (
                      <ImageGallery imagens={otherData.imagens} alt={alt} />
                    ) : null}
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 p-4 border-t sm:border-none static sm:absolute sm:bottom-0">
                    <div className="flex flex-row gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => open(TABS_ESTATE.fotos.value)}
                      >
                        <CameraIcon className="w-4 h-4 mr-2" />
                        Fotos
                      </Button>
                      <Button variant="secondary" size="sm" asChild>
                        <a href={mapUrl} target="_blank" rel="nofollow">
                          <MapPinIcon className="w-4 h-4 mr-2" />
                          Localização
                        </a>
                      </Button>
                      <FavoriteButton
                        id={otherData.id}
                        favorito={otherData.favorito}
                      />
                    </div>
                    <div className="block sm:hidden absolute bottom-[75px]">
                      <CreateRegistry
                        data={data}
                        url={matriculaUrl}
                        size="sm"
                        className="shadow-xl max-w-max"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-12">
                  <section
                    id="area"
                    className="flex flex-row flex-wrap gap-4 sm:gap-8"
                  >
                    {otherData.total_area && otherData.total_area > 0 ? (
                      <div className="flex flex-row gap-3 items-start sm:items-center">
                        <HomeIcon className="w-6 h-6" aria-hidden="true" />
                        <div className="flex flex-col gap-1">
                          <p className="font-bold">Total de área</p>
                          <p>{otherData.total_area}m²</p>
                        </div>
                      </div>
                    ) : null}
                    {otherData.total_area_privativa &&
                    otherData.total_area_privativa > 0 ? (
                      <div className="flex flex-row gap-3 items-start sm:items-center">
                        <HomeIcon className="w-6 h-6" aria-hidden="true" />
                        <div className="flex flex-col gap-1">
                          <p className="font-bold">Área privativa</p>
                          <p>{otherData.total_area_privativa}m²</p>
                        </div>
                      </div>
                    ) : null}
                    {otherData.total_area_terreno &&
                    otherData.total_area_terreno > 0 ? (
                      <div className="flex flex-row gap-3 items-start sm:items-center">
                        <RulerSquareIcon
                          className="w-6 h-6"
                          aria-hidden="true"
                        />
                        <div className="flex flex-col gap-1">
                          <p className="font-bold">Área do terreno</p>
                          <p>{otherData.total_area_terreno}m²</p>
                        </div>
                      </div>
                    ) : null}
                  </section>
                  <section id="detalhes">
                    <EstateTitle title="Detalhes">
                      {otherData.descricao.length > 1 ? (
                        <h2 className="text-muted-foreground">
                          {otherData.descricao}
                        </h2>
                      ) : null}
                      {otherData.averbacao &&
                      otherData.banco === banks.santander.value ? (
                        <p className="text-muted-foreground">
                          {otherData.averbacao}
                        </p>
                      ) : null}
                      {otherData.banco === banks.caixa.value ||
                      otherData.banco === banks.santander.value ? (
                        <ul className="flex flex-col gap-1 list-disc">
                          <li className="text-muted-foreground flex items-center">
                            {otherData.aceita_fgts ? (
                              <>
                                <div className="mr-2 text-green-500">✓</div>
                                <h3>
                                  Permite utilização de FGTS (Consulte condições
                                  no edital)
                                </h3>
                              </>
                            ) : (
                              <>
                                <div className="mr-2 text-red-500">✗</div>
                                <h3>Não permite utilização de FGTS</h3>
                              </>
                            )}
                          </li>
                          <li className="text-muted-foreground flex items-center">
                            {otherData.aceita_financiamento ? (
                              <>
                                <div className="mr-2 text-green-500">✓</div>
                                <h3>
                                  Permite financiamento (Consulte condições no
                                  edital)
                                </h3>
                              </>
                            ) : (
                              <>
                                <div className="mr-2 text-red-500">✗</div>
                                <h3>Não permite financiamento</h3>
                              </>
                            )}
                          </li>
                          {otherData.aceita_a_vista ? (
                            <li className="text-muted-foreground flex items-center">
                              <div className="mr-2 text-green-500">✓</div>
                              <h3>
                                Exclusivamente à vista (somente recursos
                                próprios)
                              </h3>
                            </li>
                          ) : null}
                          <li className="text-muted-foreground flex items-center">
                            {otherData.aceita_parcelamento ? (
                              <>
                                <div className="mr-2 text-green-500">✓</div>
                                <h3>
                                  Permite parcelamento (Consulte condições no
                                  edital)
                                </h3>
                              </>
                            ) : (
                              <>
                                <div className="mr-2 text-red-500">✗</div>
                                <h3>Não permite parcelamento</h3>
                              </>
                            )}
                          </li>
                          <li className="text-muted-foreground flex items-center">
                            {otherData.aceita_consorcio ? (
                              <>
                                <div className="mr-2 text-green-500">✓</div>
                                <h3>Permite consórcio</h3>
                              </>
                            ) : (
                              <>
                                <div className="mr-2 text-red-500">✗</div>
                                <h3>Não permite consórcio</h3>
                              </>
                            )}
                          </li>
                          {otherData.banco === banks.caixa.value ? (
                            <li className="text-muted-foreground flex items-center">
                              {otherData.area_nao_averbada ? (
                                <>
                                  <div className="mr-2 text-red-500">✗</div>
                                  <h3>Existe área não averbada</h3>
                                </>
                              ) : (
                                <>
                                  <div className="mr-2 text-green-500">✓</div>
                                  <h3>Área averbada</h3>
                                </>
                              )}
                            </li>
                          ) : null}
                          {otherData.situacao ? (
                            <li className="text-muted-foreground flex items-center">
                              <div className="mr-2 text-green-500">✓</div>
                              <h3 className="capitalize">
                                {otherData.situacao}
                              </h3>
                            </li>
                          ) : null}
                          {otherData.acoes_judiciais.length > 0 ? (
                            <li className="text-muted-foreground">
                              Ações judiciais:{" "}
                              {otherData.acoes_judiciais.map(
                                (item: string, index: number) => (
                                  <Fragment key={item}>
                                    <a
                                      href={`https://www.jusbrasil.com.br/busca?q=${item}`}
                                      target="_blank"
                                      rel="nofollow"
                                      className="text-base text-muted-foreground underline"
                                    >
                                      {item}
                                    </a>
                                    {index <
                                      otherData.acoes_judiciais.length - 1 &&
                                      ", "}
                                  </Fragment>
                                )
                              )}
                            </li>
                          ) : null}
                        </ul>
                      ) : null}
                    </EstateTitle>
                  </section>
                  <section id="matriculas">
                    <Card className="bg-blue-50 border-blue-200 w-full">
                      <CardContent className="flex flex-col gap-2 p-4">
                        <p className="font-semibold">Matrícula do imóvel</p>
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <p className="text-xs text-gray-600">Comarca:</p>
                            <p className="text-sm">{otherData.comarca}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-600">
                              Matrícula(s):
                            </p>
                            {otherData.matricula ? (
                              <span className="text-sm">
                                {otherData.matricula}
                              </span>
                            ) : (
                              <div className="flex flex-row gap-1 items-center">
                                <span className="text-sm">Não informada</span>
                                <RegisterTooltip />
                              </div>
                            )}
                          </div>
                          <div className="col-span-2">
                            <p className="text-xs text-gray-600">
                              Inscrição imobiliária:
                            </p>
                            {otherData.inscricao_imobiliaria ? (
                              <>
                                <span className="text-sm">
                                  {otherData.inscricao_imobiliaria}
                                </span>
                                <OpenLinkOutside href={iptuUrl} />
                              </>
                            ) : (
                              <div className="flex flex-row gap-1 items-center">
                                <span className="text-sm">Não informada</span>
                                <RegisterTooltip />
                              </div>
                            )}
                          </div>
                          {otherData.averbacao &&
                          otherData.banco === banks.caixa.value ? (
                            <div className="col-span-2">
                              <p className="text-xs text-gray-600">
                                Averbação dos leilões negativos:
                              </p>
                              <p className="text-sm">{otherData.averbacao}</p>
                            </div>
                          ) : null}
                        </div>
                        <CreateRegistry
                          data={data}
                          url={matriculaUrl}
                          size="sm"
                          className="shadow-xl max-w-max"
                        />
                      </CardContent>
                    </Card>
                  </section>
                  <div className="block sm:hidden">
                    <AuctionDetails
                      data={data}
                      hasPaidPlan={hasPaidPlan}
                      openInfo={open}
                    />
                  </div>
                  <section id="documentos" className="scroll-mt-36">
                    <EstateTitle title="Documentos">
                      {editalUrl || matriculaUrl ? (
                        <div className="flex flex-row flex-wrap gap-3">
                          {editalUrl ? (
                            <Button
                              asChild
                              variant="outline"
                              className="h-auto py-4 px-6 flex flex-col items-center"
                            >
                              <a
                                href={editalUrl}
                                target="_blank"
                                rel="nofollow"
                              >
                                <DocumentIcon
                                  className="w-6 h-6 mb-2"
                                  aria-hidden="true"
                                />
                                <span className="text-sm">
                                  Edital do Leilão
                                </span>
                              </a>
                            </Button>
                          ) : null}
                          {matriculaUrl ? (
                            <Button
                              asChild
                              variant="outline"
                              className="h-auto py-4 px-6 flex flex-col items-center"
                            >
                              <a
                                href={matriculaUrl}
                                target="_blank"
                                rel="nofollow"
                              >
                                <DocumentIcon
                                  className="w-6 h-6 mb-2"
                                  aria-hidden="true"
                                />
                                <span className="text-sm">
                                  Matrícula do Imóvel
                                </span>
                              </a>
                            </Button>
                          ) : null}
                        </div>
                      ) : null}
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center text-sm text-muted-foreground">
                          <div>
                            <InformationCircleIcon
                              className="w-4 h-4 mr-2"
                              aria-hidden="true"
                            />
                          </div>
                          <span>
                            Leia atentamente o Edital do leilão. Ficou com
                            alguma dúvida?{" "}
                            <Link
                              prefetch={false}
                              href="/ferramentas/glossario"
                              target="_blank"
                              className="text-primary text-sm underline"
                            >
                              Acesse o nosso glossário.
                            </Link>
                          </span>
                        </div>
                        <div className="flex items-center text-sm text-muted-foreground">
                          <div>
                            <InformationCircleIcon className="w-4 h-4 mr-2" />
                          </div>
                          <span>
                            Ver mais informações no{" "}
                            <a
                              href={otherData.link}
                              target="_blank"
                              rel="nofollow"
                              className="text-primary text-sm underline"
                            >
                              site do leiloeiro
                            </a>
                          </span>
                        </div>
                      </div>
                    </EstateTitle>
                  </section>
                  <section id="comparativo-de-mercado">
                    <EstateTitle title="Precificação:">
                      <p className="text-sm">
                        Consulte os valores de imóveis no mesmo endereço:
                      </p>
                      <ul className="flex flex-col gap-2">
                        <li className="text-muted-foreground">
                          <OpenLinkOutside href={zapUrl} display="inline-flex">
                            <img
                              src="/logos/zapimovel-logo.svg"
                              alt="ZAP Imóveis"
                              width={24}
                              height={24}
                              className="mr-2"
                            />
                            <a
                              href={zapUrl}
                              target="_blank"
                              rel="nofollow"
                              className="text-base text-muted-foreground hover:underline"
                            >
                              Pesquisar no ZAP Imóveis
                            </a>
                          </OpenLinkOutside>
                        </li>
                        <li className="text-muted-foreground">
                          <OpenLinkOutside
                            href={vivarealUrl}
                            display="inline-flex"
                          >
                            <img
                              src="/logos/vivareal-logo.svg"
                              alt="Viva Real"
                              width={24}
                              height={24}
                              className="mr-2"
                            />
                            <a
                              href={vivarealUrl}
                              target="_blank"
                              rel="nofollow"
                              className="text-base text-muted-foreground hover:underline"
                            >
                              Pesquisar no Viva Real
                            </a>
                          </OpenLinkOutside>
                        </li>
                        <li className="text-muted-foreground">
                          <OpenLinkOutside
                            href={imovelwebUrl}
                            display="inline-flex"
                          >
                            <img
                              src="/logos/imovelweb-logo.svg"
                              alt="Imovelweb"
                              width={24}
                              height={24}
                              className="mr-2"
                            />
                            <a
                              href={imovelwebUrl}
                              target="_blank"
                              rel="nofollow"
                              className="text-base text-muted-foreground hover:underline"
                            >
                              Pesquisar no Imovelweb
                            </a>
                          </OpenLinkOutside>
                        </li>
                      </ul>
                      <div className="flex flex-col gap-2">
                        <p className="text-sm text-muted-foreground">
                          Com base nos valores que encontramos, estimamos um
                          lucro de:
                        </p>
                        {preco_atual ? (
                          <span
                            className={cn(
                              "text-2xl",
                              otherData.valor_lucro_liquido_com_corretor > 0
                                ? "text-green-600"
                                : "text-red-600"
                            )}
                          >
                            <span className="font-semibold">
                              {formatBRL(
                                otherData.valor_lucro_liquido_com_corretor
                              )}
                            </span>{" "}
                            <span className="text-xs text-muted-foreground">
                              (ROI: {`${otherData.roi_com_corretor}%`})
                            </span>
                          </span>
                        ) : (
                          <span>A definir</span>
                        )}
                        <p className="text-sm text-muted-foreground">
                          Antes de arrematar, faça um cálculo detalhado na{" "}
                          <Button
                            variant="link"
                            className="p-0 min-h-6 underline"
                            onClick={() => {
                              setActiveTab(TABS_ESTATE.calculadora.value);
                              scrollToTop();
                            }}
                          >
                            calculadora de viabilidade financeira.
                          </Button>
                        </p>
                      </div>
                    </EstateTitle>
                  </section>
                  <EstateTitle title="Outras informações">
                    {showRegion ? (
                      <section
                        id="regiao"
                        className="flex flex-row flex-wrap gap-6"
                      >
                        <div className="flex flex-col gap-2">
                          <p className="text-muted-foreground text-sm">
                            Estado
                          </p>
                          <a
                            href={`/leilao-de-imoveis/${uf}`}
                            target="_blank"
                            className="font-medium uppercase hover:underline"
                          >
                            {uf}
                          </a>
                        </div>
                        <div className="flex flex-col gap-2">
                          <p className="text-muted-foreground text-sm">
                            Cidade
                          </p>
                          <a
                            href={`/leilao-de-imoveis/${uf}?cidade=${cidade_descricao}`}
                            target="_blank"
                            className="font-medium hover:underline"
                          >
                            {cidade_descricao}
                          </a>
                        </div>
                        <div className="flex flex-col gap-2">
                          <p className="text-muted-foreground text-sm">
                            Bairro
                          </p>
                          <p className="font-medium">
                            <a
                              href={`/leilao-de-imoveis/${uf}?pesquisa=${bairro}`}
                              target="_blank"
                              className="font-medium hover:underline"
                            >
                              {bairro}
                            </a>
                          </p>
                        </div>
                        <div className="flex flex-col gap-2">
                          <p className="text-muted-foreground text-sm">Tipo</p>
                          <a
                            href={`/leilao-de-imoveis/${uf}?type=${otherData.tipo}`}
                            target="_blank"
                            className="font-medium hover:underline"
                          >
                            {upperFirst(otherData.tipo)}
                          </a>
                        </div>
                      </section>
                    ) : null}
                    <section
                      id="banco"
                      className="flex flex-row flex-wrap gap-6 mb-12"
                    >
                      <div className="flex flex-col gap-2">
                        <p className="text-muted-foreground text-sm">Credor</p>
                        <a
                          href={`/leilao-de-imoveis/${uf}?banco=${otherData.banco}`}
                          target="_blank"
                          className="font-medium hover:underline"
                        >
                          {bankCriteria[otherData.banco]}
                        </a>
                      </div>
                      {otherData.modalidade_de_venda ? (
                        <div className="flex flex-col gap-2">
                          <p className="text-muted-foreground text-sm">
                            Modalidade de venda
                          </p>
                          <a
                            href={`/leilao-de-imoveis/${uf}?banco=${otherData.banco}&modalidade=${modalityReverseCriteria[otherData.modalidade_de_venda as Modality]}`}
                            target="_blank"
                            className="font-medium hover:underline"
                          >
                            {otherData.modalidade_de_venda}
                          </a>
                        </div>
                      ) : null}
                    </section>
                    <section id="consideracoes-legais">
                      <p className="text-muted-foreground">
                        <strong>IMPORTANTE:</strong> As informações do leilão
                        foram retiradas da{" "}
                        <a
                          href={otherData.link}
                          target="_blank"
                          rel="nofollow"
                          className="hover:underline"
                        >
                          página oficial do imóvel
                        </a>
                        . Antes de tomar qualquer decisão, confirme as
                        informações no site do leiloeiro oficial do imóvel.{" "}
                        <a
                          href={`/leilao-de-imoveis/${uf}`}
                          target="_blank"
                          className="hover:underline"
                        >
                          Veja todos os leilões de imóveis disponíveis no Pro
                          Leilão.
                        </a>
                      </p>
                    </section>
                    <section
                      id="grupo-telegram"
                      className="flex flex-col gap-4 mb-12"
                    >
                      <h4 className="text-lg font-semibold">
                        Quer receber alertas de leilões de imóveis parecidos com
                        este imóvel no seu celular?
                      </h4>
                      <div>
                        <Button
                          variant="secondary"
                          className="flex flex-row gap-2 w-fit"
                          asChild
                        >
                          <a
                            href={telegramGroup[uf].telegram.link}
                            target="_blank"
                            rel="nofollow"
                          >
                            <PaperAirplaneIcon
                              className="w-4 h-4 text-muted-foreground"
                              aria-hidden="true"
                            />
                            Entrar no grupo do Telegram
                          </a>
                        </Button>
                      </div>
                    </section>
                    <h2 className="text-muted-foreground">
                      {formatPropertyTitle(
                        {
                          tipo: otherData.tipo,
                          cidade_descricao,
                          bairro,
                          banco: otherData.banco,
                        },
                        { showBank: true, showAuction: true }
                      )}
                      , {endereco}. {bankDescription[otherData.banco]}
                    </h2>
                    <section id="palavras-chave">
                      <h2 className="text-xs text-muted-foreground">
                        {generateKeywords({
                          baseArea: uf,
                          bankInfo: otherData.banco,
                          citiesSearchParams: cidade_descricao,
                          type: otherData.tipo,
                          endereco: endereco,
                          modalities:
                            modalityReverseCriteria[
                              otherData.modalidade_de_venda as Modality
                            ],
                        }).join(". ")}
                      </h2>
                    </section>
                  </EstateTitle>
                  <AdSenseResponsive
                    orientation="horizontal"
                    variant="property"
                  />
                </div>
              </div>
              {/* Right Column - Auction Details */}
              <div className="hidden sm:block">
                <AuctionDetails
                  data={data}
                  hasPaidPlan={hasPaidPlan}
                  openInfo={open}
                />
              </div>
            </div>
          </main>
        </TabsContent>
        <TabsContent value={TABS_ESTATE.calculadora.value}>
          <main className="w-full">
            <ViabilityList
              hasPaidPlan={hasPaidPlan}
              hideShare
              data={{
                leilao_id: otherData.id,
                titulo: "",
                valor_arrematacao: preco_atual || 0,
                valor_avaliacao: valor_de_avaliacao || 0,
                banco: otherData.banco,
                uf,
                tipo: otherData.tipo,
                modalidade_de_venda: otherData.modalidade_de_venda as Modality,
              }}
              onSave={scrollToTop}
            />
          </main>
        </TabsContent>
        <TabsContent value={TABS_ESTATE.matricula.value}>
          <main className="w-full flex flex-col gap-10">
            <Registry
              id={otherData.numero_do_leilao}
              field="numero_do_leilao"
              emptyElement={
                <CardEmpty
                  title="Análise de matrícula com IA"
                  description="A análise da matrícula deste imóvel ainda não foi realizada. Não perca tempo e faça agora mesmo."
                  icon={<DocumentIcon className="h-8 w-8 text-primary" />}
                >
                  <CreateRegistry data={data} url={matriculaUrl} />
                </CardEmpty>
              }
            />
          </main>
        </TabsContent>
        <TabsContent value={TABS_ESTATE.edital.value}>
          <main className="w-full flex flex-col gap-10">
            <Notice
              id={otherData.numero_do_leilao}
              field="numero_do_leilao"
              emptyElement={
                <CardEmpty
                  title="Análise de edital com IA"
                  description="A análise do edital deste imóvel ainda não foi realizada. Não perca tempo e faça agora mesmo."
                  icon={<DocumentIcon className="h-8 w-8 text-primary" />}
                >
                  <CreateNotice data={data} url={editalUrl} />
                </CardEmpty>
              }
            />
          </main>
        </TabsContent>
        <TabsContent value={TABS_ESTATE.diligencia.value}>
          <main className="w-full">
            <Diligence
              id={otherData.numero_do_leilao}
              field="numero_do_leilao"
              emptyElement={
                <CardEmpty
                  title="Análise de matrícula com IA"
                  description="A análise da matrícula deste imóvel ainda não foi realizada. Não perca tempo e faça agora mesmo."
                  icon={<DocumentIcon className="h-8 w-8 text-primary" />}
                >
                  <CreateRegistry data={data} url={matriculaUrl} />
                </CardEmpty>
              }
            />
          </main>
        </TabsContent>
      </Tabs>
      <InfoDialog data={data} visible={activeTab} onClose={close} />
    </main>
  );
};
